import { useAuth } from 'contexts/auth';
import { createContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import userRequests from 'services/user';
import { getRandomProfileCircle } from 'utils/helper';

const defaultForm = {
    firstName: '',
    lastName: '',
    preferredName: '',
    password: '',
    confirmPassword: '',
    courseName: '',
};

const PAGES = {
    NAME: 'enter-name',
    NEW_PASSWORD: 'new-password',
    COURSE: 'course',
};

const PageContext = createContext();

const PageProvider = (props) => {
    const history = useHistory();
    const { auth, updateCurrentUser, updatePassword } = useAuth();
    const { t } = useTranslation();

    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [errors, setErrors] = useState({
        'enter-name': {
            firstName: '',
            lastName: '',
            preferredName: '',
        },
        'new-password': {
            password: '',
            confirmPassword: '',
        },
        course: {
            courseName: '',
        },
    });
    const [currentStep, setCurrentStep] = useState(PAGES.NAME);
    const [satisfyRequirement, setSatisfyRequirement] = useState({
        validateMoreThan8Characters: true,
        validateOneUpperCase: true,
        validateOneLowerCase: true,
        validateOneNumber: true,
        validateOneSpecial: true,
    });

    const handlePrev = (step) => {
        return setCurrentStep(step);
    };

    const handleNext = (step) => {
        if (!validateAll()) return;
        setCurrentStep(step);
    };

    const handleComplete = async () => {
        if (!validateAll()) return;
        setLoading(true);
        const updateUserObj = {
            display_name: form.preferredName || '',
            first_name: form.firstName,
            last_name: form.lastName,
            profile_circle: getRandomProfileCircle(),
            profile_set_up: true,
        };
        const getUser = await userRequests.getUser(auth?.user_id);
        await updatePassword(getUser.password, form.password);
        await updateCurrentUser(updateUserObj);
        history.push({ pathname: '/', state: { showWelcomeDialog: true } });
    };

    const handleChange = async (key, data) => {
        setForm((current) => ({ ...current, [key]: data }));
    };

    const handleNewPasswordRequirement = (password) => {
        const atLeastOneUpperCase = /^.*[A-Z]+.*$/;
        const atLeastOneLowerCase = /^.*[a-z]+.*$/;
        const atLeastOneNumber = /^.*[0-9]+.*$/;
        const atLeast8Character = /^[a-zA-Z0-9!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]{8,20}$/;
        const atLeastOneSpecial = /^(?=.*[!@#$%^&*]).*$/;
        const validateOneUpperCase = atLeastOneUpperCase.exec(password);
        const validateOneLowerCase = atLeastOneLowerCase.exec(password);
        const validateOneNumber = atLeastOneNumber.exec(password);
        const validateMoreThan8Characters = atLeast8Character.exec(password);
        const validateOneSpecial = atLeastOneSpecial.exec(password);
        setSatisfyRequirement({
            validateOneUpperCase: !validateOneUpperCase ? true : false,
            validateOneLowerCase: !validateOneLowerCase ? true : false,
            validateOneNumber: !validateOneNumber ? true : false,
            validateMoreThan8Characters: !validateMoreThan8Characters ? true : false,
            validateOneSpecial: !validateOneSpecial ? true : false,
        });
    };

    const getErrorMessage = (key, value, form) => {
        value = value?.trim();
        switch (key) {
            case 'firstName':
                return value ? '' : t('account-settings.profile.enter-first-name', 'Please enter a first name');
            case 'lastName':
                return value ? '' : t('account-settings.profile.enter-last-name', 'Please enter a last name');
            case 'password':
                if (Object.values(satisfyRequirement).find((requirement) => requirement))
                    return t(
                        'account-settings.password.please-satisfy-requirements',
                        'Please satisfy all requirements'
                    );
                return value ? '' : t('account-settings.password.enter-password', 'Please enter password');
            case 'confirmPassword':
                return form.password === value
                    ? ''
                    : t('account-settings.password.passwords-mismatch', 'Passwords do not match');
            default:
                return '';
        }
    };

    const validate = (key, value) => {
        const errorMessage = getErrorMessage(key, value, form);
        setErrors((errors) => {
            return {
                ...errors,
                [key]: errorMessage,
            };
        });
    };

    const validateAll = () => {
        return !Boolean(
            Object.keys(errors[currentStep])
                .map((key) => {
                    validate(key, form[key]);
                    return getErrorMessage(key, form[key], form);
                })
                .reduce((cur, acc) => {
                    return cur + acc;
                }, '')
        );
    };

    useEffect(() => {
        if (form?.password.length) {
            handleNewPasswordRequirement(form?.password);
        }
    }, [form?.password]);

    return (
        <PageContext.Provider
            value={{
                loading,
                form,
                errors,
                currentStep,
                satisfyRequirement,

                handleChange,
                handlePrev,
                handleNext,
                handleComplete,
                setErrors,
                setForm,
                PAGES,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
